import { Injectable } from '@angular/core'

import {
  AssessmentLevel,
  IRoleplayAssessmentParser,
  RoleplayAssessmentData,
} from './roleplay-assessment.types'
import { AIResponse } from '../websocket.service'

type AIResponseWithSnakeCaseReasonDetail = AIResponse & {
  reason_detail?: unknown
}

const VALID_ASSESSMENT_LEVELS: readonly AIResponse['assessment'][] = [
  'Bad',
  'Normal',
  'Good',
  'Excellent',
]

/**
 * AIレスポンスの評価データパースサービス
 *
 * WebSocketから受信した生JSONオブジェクトの中から評価関連プロパティを抽出し、
 * バリデーション後に型安全なRoleplayAssessmentDataに変換する。
 *
 * 既存のAIResponseインターフェースを変更せず、ランタイムのプロパティアクセスのみで動作する。
 */
@Injectable({
  providedIn: 'root',
})
export class RoleplayAssessmentParserService implements IRoleplayAssessmentParser {
  /**
   * AIレスポンスの生データオブジェクトから評価データを抽出する。
   * 評価プロパティが一切存在しない場合はundefinedを返却する。
   *
   * @param rawData - WebSocketから受信した生JSONオブジェクト
   * @returns パース済み評価データ、または評価プロパティが存在しない場合はundefined
   */
  parse(rawData: AIResponse): RoleplayAssessmentData | undefined {
    const speaker = this.parseString(rawData['speaker'])
    const assessment = this.parseAssessmentLevel(rawData['assessment'])
    const reason = this.parseString(rawData['reason'])
    const reasonDetail = this.parseReasonDetail(rawData)
    const score = this.parseScore(rawData['score'])
    const isFinished = this.parseBoolean(rawData['isFinished'])

    // 全プロパティがundefinedの場合は評価データなしとしてundefinedを返却
    if (
      speaker == null &&
      assessment == null &&
      reason == null &&
      reasonDetail == null &&
      score == null &&
      isFinished == null
    ) {
      return undefined
    }

    return {
      speaker,
      assessment,
      reason,
      reasonDetail,
      score,
      isFinished,
    }
  }

  private parseString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
    return undefined
  }

  private parseAssessmentLevel(
    value: AIResponse['assessment'],
  ): AssessmentLevel | undefined {
    if (typeof value === 'string' && VALID_ASSESSMENT_LEVELS.includes(value)) {
      return value as AssessmentLevel
    }
    return undefined
  }

  private parseReasonDetail(rawData: AIResponse): string | undefined {
    // camelCase（reasonDetail）を優先し、なければsnake_case（reason_detail）を使用
    const camelCase = rawData['reasonDetail']
    if (typeof camelCase === 'string' && camelCase.length > 0) {
      return camelCase
    }

    // 想定外に snakeCase で出力された場合に対応する
    const snakeCase = (rawData as AIResponseWithSnakeCaseReasonDetail)[
      'reason_detail'
    ]
    if (typeof snakeCase === 'string' && snakeCase.length > 0) {
      return snakeCase
    }

    return undefined
  }

  private parseScore(value: unknown): number | undefined {
    if (typeof value !== 'number' || isNaN(value)) {
      return undefined
    }
    return Math.max(0, Math.min(100, value))
  }

  private parseBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') {
      return value
    }
    return undefined
  }
}
